import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';

import { brandText } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import { AccountRevenueSharingInterFace } from '../../../interface/revenue-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableByTransactionFilterApplied,
  fetchByTransactionAccount,
  fetchByTransactionAccountFilters,
  setSortData,
  updateByTransactionFilters,
} from '../../../redux/slices/ByTransactionSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween, removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import revenueSharingApi from '../../../utils/api/revenue';

/**
 * By transaction data table component.
 * @returns {React.JSX.Element} The rendered ByTransactionAccount component.
 */
function ByTransactionAccount() {
  const { t, i18n } = useTranslation(['revenue', 'account']);
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const accountCustomId = id || searchParameters.get('accountCustomId');
  const {
    ByTransaction,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.byTransaction);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
        />
      </div>
    );
  };

  /**
   * Fetch revenue sharing transactions list.
   * @param {any} data - The request data.
   */
  const fetchData = (data: any) => {
    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitError'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }
    if (!loading && accountCustomId) {
      dispatch(
        fetchByTransactionAccount({
          accountCustomIds: [accountCustomId],
          ...data,
        }),
      );
    }
  };

  /**
   * Function to refresh data.
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      /**
       * Rendering converted UTC date and time.
       * @param {AccountRevenueSharingInterFace} RowData - Current row data.
       * @returns {React.JSX.Element} Returns span element with date and time.
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ConvertUTCtoDateTime(RowData.createdAt)}</span>
      ),
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: t('accountId'),
      accessorKey: 'accountCustomId' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStationName' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: `${t('totalPowerConsumed')} (kWh)`,
      accessorKey: 'totalPowerConsumedInKwh' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      /**
       * Rendering total power consumption.
       * @param {AccountRevenueSharingInterFace} RowData - The row data.
       * @returns {React.JSX.Element} The rendered component.
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.powerConsumedInKwh, 3)} kWh</span>
      ),
    },
    {
      label: t('transactionId'),
      accessorKey: 'transactionId' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
    },
    {
      label: `${t('totalAmount')} (${brandText.currency})`,
      accessorKey: 'totalAmount' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      columnWidth: 160,
      /**
       * Rendering total amount.
       * @param {AccountRevenueSharingInterFace} RowData - The row data.
       * @returns {React.JSX.Element} The rendered component.
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>
          {ToFixedValue(RowData?.totalAmount)} {brandText.currency}
        </span>
      ),
    },
    {
      label: t('energyCost'),
      accessorKey: 'energyCost' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Rendering energy cost
       * @param {any} RowData - The row data
       * @returns {any} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.energyCost)}</span>
      ),
    },
    {
      label: t('sharableRevenue'),
      accessorKey: 'sharableRevenue' as keyof AccountRevenueSharingInterFace,
      sortAllow: true,
      /**
       * Rendering sharable revenue
       * @param {any} RowData - The row data
       * @returns {any} The rendered component
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.sharableRevenue)}</span>
      ),
    },
    {
      label: t('partnerRevenueByPercentage'),
      accessorKey: 'partnerRevenuePercentage' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Rendering partner revenue by percentage.
       * @param {AccountRevenueSharingInterFace} RowData - The row data.
       * @returns {React.JSX.Element} The rendered component.
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.partnerRevenuePercentage)} %</span>
      ),
    },
    {
      label: `${t('partnerRevenueByCost')} (${brandText.currency})`,
      accessorKey: 'partnerRevenue' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Rendering partner revenue cost.
       * @param {AccountRevenueSharingInterFace} RowData - The row data.
       * @returns {React.JSX.Element} The rendered component.
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>
          {ToFixedValue(RowData?.partnerRevenue)} {brandText.currency}
        </span>
      ),
    },
    {
      label: t('cpoRevenueByPercentage'),
      accessorKey: 'cpoRevenuePercentage' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      /**
       * Rendering CPO revenue by percentage.
       * @param {AccountRevenueSharingInterFace} RowData - The row data.
       * @returns {React.JSX.Element} The rendered component.
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>{ToFixedValue(RowData?.cpoRevenuePercentage)} %</span>
      ),
    },
    {
      label: `${t('cpoRevenueByCost')} (${brandText.currency})`,
      accessorKey: 'cpoRevenue' as keyof AccountRevenueSharingInterFace,
      sortAllow: false,
      columnWidth: 190,
      /**
       * Rendering CPO revenue by cost.
       * @param {AccountRevenueSharingInterFace} RowData - The row data.
       * @returns {React.JSX.Element} The rendered component.
       */
      customData: (RowData: AccountRevenueSharingInterFace) => (
        <span>
          {ToFixedValue(RowData?.cpoRevenue)} {brandText.currency}
        </span>
      ),
    },
  ];

  /**
   * Column definitions for the table export.
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('accountName'), accessorKey: 'accountName' },
    { label: t('accountId'), accessorKey: 'accountCustomId' },
    { label: t('stationId'), accessorKey: 'chargingStationId' },
    { label: `${t('totalPowerConsumed')} (kWh)`, accessorKey: 'totalMeterDifference' },
    { label: t('transactionId'), accessorKey: 'transactionId' },
    { label: `${t('totalAmount')} (${brandText.currency})`, accessorKey: 'totalAmount' },
    { label: t('partnerRevenueByPercentage'), accessorKey: 'partnerRevenuePercentage' },
    { label: t('partnerRevenueByCost'), accessorKey: 'partnerRevenue' },
    { label: t('cpoRevenuePercentage'), accessorKey: 'cpoRevenuePercentage' },
    { label: t('cpoRevenueByCost'), accessorKey: 'cpoRevenue' },
  ];

  /**
   * Filtering columns for export without hidden columns.
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Table pagination limit change function.
   * @param {any} value - The pagination parameter object.
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  /**
   * Export data function for pdf and csv.
   * @param {string} type - Export format ('csv' | 'pdf').
   */
  const exportDataPdfOrCSV = (type: string) => {
    if (!accountCustomId) return;
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = removeEmptyKeys({
      accountCustomIds: [accountCustomId],
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy,
      sortField,
    });
    revenueSharingApi
      .fetchRevenueSharingExport(payload)
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: AccountRevenueSharingInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row.createdAt ? ConvertUTCtoDateTime(row.createdAt) : t('na');

              case 'accountCustomId':
                return row.accountCustomId || t('na');

              case 'totalMeterDifference':
                return row.powerConsumedInKwh
                  ? ToFixedValue(Number(row.powerConsumedInKwh), 3)
                  : '0.000';

              case 'totalAmount':
                return row.totalAmount ? ToFixedValue(row.totalAmount, 2) : '0.00';

              case 'transactionId':
                return row.transactionId ? row.transactionId : '0';

              case 'partnerRevenuePercentage':
                return row.partnerRevenuePercentage
                  ? ToFixedValue(row.partnerRevenuePercentage, 2)
                  : '0.00';

              case 'partnerRevenue':
                return row.partnerRevenue ? ToFixedValue(row.partnerRevenue, 2) : '0.00';

              case 'cpoRevenuePercentage':
                return row.cpoRevenuePercentage
                  ? ToFixedValue(row.cpoRevenuePercentage, 2)
                  : '0.00';

              case 'cpoRevenue':
                return row.cpoRevenue ? ToFixedValue(row.cpoRevenue, 2) : '0.00';

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'revenue-sharing-list.csv');
          } else {
            exportPDF(headers, rows, 'revenue-sharing-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code || 404,
          }),
        );
      });
  };

  /**
   * Handle the sort data function.
   * @param {any} sortData - The sorting configuration.
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (
      isFilterUpdated ||
      ByTransaction.length === 0 ||
      dateRange.startDate ||
      dateRange.endDate ||
      accountCustomId
    ) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...queryParameters,
        sortBy,
        sortField,
      });
      dispatch(disableByTransactionFilterApplied());
    }
  }, [filters, dateRange.endDate, dateRange.startDate, accountCustomId]);

  return (
    <div className="table-content" dir={i18n.dir()}>
      <CustomTable
        version="v2"
        header={column}
        data={ByTransaction}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        pageLimitChange={pageNateLimitChange}
        handleSortChange={handleSortFunction}
        onHiddenColumnsChange={setHiddenColumns}
        filters={filters}
        filtersToDispatch={updateByTransactionFilters}
        filterListLoading={filterListLoading}
        exportDataPdfOrCSV={
          permissionCheck('cpoRevenueSharingExport') ? exportDataPdfOrCSV : undefined
        }
        filterListToDispatch={fetchByTransactionAccountFilters}
        exportLoader={exportLoader}
        exportData={ByTransaction}
        refershCurrentPages={handleRefresh}
        refreshRequired
        hoverLabel={t('refresh')}
        sizeChangeButtonRequired
        height={600}
        rightTopContent={renderDateFilters()}
        onClearFilterChange={() => {
          clearDateRange();
        }}
      />
    </div>
  );
}

export default ByTransactionAccount;
